import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  BASEMAP_ESRI_SATELLITE,
  BASEMAP_GOOGLE_HYBRID,
  BASEMAP_GOOGLE_ROADMAP,
} from '@rsos/constants/basemapAddonNames';
import { GOOGLE } from '@rsos/constants/mapProviders';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { setGoogleMapZoomTo } from '@rsos/utils/panMapToCoordinate';

/**
 * Google basemap transition handling
 * @param {Object} mapInstance - The Google map instance object.
 */
const useGoogleMapTransition = mapInstance => {
  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const selectedCallerID = useSelector(
    state => state?.emergencyCalls?.selectedCallerID,
  );

  const selectedAlertID = useSelector(
    state => state?.incidents?.selectedAlertID,
  );

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );

  const callQueue = useSelector(state =>
    state?.emergencyCalls?.callQueue
      ? callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess)
      : {},
  );

  const hasLocation = !!getMostRecentLoc(callQueue?.[selectedCallerID]);

  const isAllView = useMemo(() => {
    return (!selectedCallerID || !hasLocation) && !selectedAlertID;
  }, [hasLocation, selectedAlertID, selectedCallerID]);

  // Update the Google basemap between 'roadmap' and 'hybrid'.
  useEffect(() => {
    if (basemap && mapInstance && mapProvider === GOOGLE) {
      mapInstance.setMapTypeId(
        basemap === BASEMAP_GOOGLE_ROADMAP
          ? BASEMAP_GOOGLE_ROADMAP
          : BASEMAP_GOOGLE_HYBRID,
      );
    }
  }, [basemap, mapInstance, mapProvider]);

  // When the mapProvider is switched to Google via the basemap menu,
  // update the map bounds to the specific view state where the user was on Esri map.
  useEffect(() => {
    if (
      mapInstance &&
      mapProvider === GOOGLE &&
      window?.__RSOS?.mapViewState &&
      isAllView
    ) {
      const { bounds, esriBasemap, zoom } = window.__RSOS.mapViewState;

      let normalizedZoom = zoom;

      // If the map provider changed from Esri to Google and it's not a satellite basemap,
      // increase the zoom level by 1 to normalize the map view.
      if (
        window?.__RSOS?.mapViewState.provider !== GOOGLE &&
        esriBasemap !== BASEMAP_ESRI_SATELLITE
      ) {
        normalizedZoom = zoom + 1;
      }

      window.google.maps.event.addListenerOnce(
        mapInstance,
        'bounds_changed', // https://developers.google.com/maps/documentation/javascript/events
        () => {
          mapInstance.fitBounds(bounds);

          setGoogleMapZoomTo(mapInstance, normalizedZoom);
        },
      );

      delete window.__RSOS.mapViewState;
    }
  }, [isAllView, mapInstance, mapProvider]);
};

export default useGoogleMapTransition;
